import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Flame, Timer, ArrowRight } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');

const splitMs = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

export const pickSoonestPromo = (plans) => {
  const list = (plans || []).filter((p) => p.promo && p.promo.ends_at).map((p) => ({ plan: p, ...p.promo }));
  if (!list.length) return null;
  return list.sort((a, b) => new Date(a.ends_at) - new Date(b.ends_at))[0];
};

export const PromoCountdown = ({ plans, showCta = true, className = '' }) => {
  const { t, i18n } = useTranslation();
  const promo = useMemo(() => pickSoonestPromo(plans), [plans]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!promo) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [promo]);
  if (!promo) return null;
  const remaining = new Date(promo.ends_at) - now;
  if (remaining <= 0) return null;
  const { d, h, m, s } = splitMs(remaining);
  const planName = promo.plan[`name_${i18n.language}`] || promo.plan.name_pt;
  const benefit = promo.kind === 'percent' ? `-${promo.value}%`
    : promo.kind === 'fixed' ? `-${promo.value.toLocaleString('pt-PT')} CVE`
      : `+${promo.value} ${t('promoBanner.bonus')}`;
  const timeText = d > 0
    ? t('promoBanner.timeDays', { d, h: pad(h), m: pad(m) })
    : t('promoBanner.timeHours', { h: pad(h), m: pad(m), s: pad(s) });
  return (
    <div className={`rounded-xl bg-slate-900 text-white px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 shadow-lg ${className}`} data-testid="promo-countdown" role="status">
      <div className="flex items-center gap-2 min-w-0">
        <Flame className="h-5 w-5 text-amber-400 flex-none animate-pulse" aria-hidden="true" />
        <p className="text-sm sm:text-[15px] leading-snug truncate">
          <span className="font-bold text-amber-300">{promo.name}</span>
          <span className="mx-1.5 text-slate-400">·</span>
          <span className="font-semibold">{benefit}</span>
          <span className="text-slate-300"> {t('promoBanner.on')} {planName}</span>
        </p>
      </div>
      <div className="flex items-center gap-2 sm:ml-auto text-sm font-mono" data-testid="promo-countdown-time">
        <Timer className="h-4 w-4 text-slate-300" aria-hidden="true" />
        <span className="text-slate-300">{t('promoBanner.endsIn')}</span>
        <span className="font-bold text-white tabular-nums">{timeText}</span>
      </div>
      {showCta && (
        <Link to="/checkout" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-amber-400 text-slate-900 text-sm font-bold hover:bg-amber-300 transition-colors flex-none" data-testid="promo-countdown-cta">
          {t('promoBanner.cta')}<ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
};
